class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedCounts = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.clickInsideResults = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.addEventListener('touchstart', this.onMouseDown.bind(this), { passive: true });

    const clearButton = this.querySelector('button.clear-search');
    if (clearButton) {
      clearButton.addEventListener('click', this.onClearSearchClick.bind(this));
    }
  }

  onClearSearchClick(event) {
    event.preventDefault();
    event.stopPropagation();
  
    this.searchTerm = '';
    this.close(true);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onMouseDown(event) {
    const resultsEl = this.predictiveSearchResults || this.querySelector('[data-predictive-search]');
    if (resultsEl && resultsEl.contains(event.target)) {
      this.clickInsideResults = true;
      const clearFlag = () => {
        setTimeout(() => { this.clickInsideResults = false; }, 150);
      };
      document.addEventListener('mouseup', clearFlag, { once: true });
      document.addEventListener('touchend', clearFlag, { once: true });
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.clickInsideResults) return;
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.code === 'Tab' || event.key === 'Tab') return;

    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const predictiveSearchUrl = (typeof routes !== 'undefined' && routes.predictive_search_url) ? routes.predictive_search_url : (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.applyCountWhenReady(searchTerm, queryKey);
      return;
    }

    const countPromise = this.fetchCountForQuery(searchTerm, queryKey);

    const url = new URL(predictiveSearchUrl, window.location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set(
      'resources[options][fields]',
      'title,tag,product_type,vendor,variants.title,body,variants.sku,variants.barcode'
    );

    fetch(url.toString(), {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const sectionEl = doc.querySelector('#shopify-section-predictive-search');
        if (!sectionEl) {
          this.close();
          return;
        }
        const resultsMarkup = sectionEl.innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        this.applyCountWhenReady(searchTerm, queryKey, countPromise);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  fetchCountForQuery(searchTerm, queryKey) {
    if (Object.prototype.hasOwnProperty.call(this.cachedCounts, queryKey)) {
      return Promise.resolve(this.cachedCounts[queryKey]);
    }

    const rootUrl = window.Shopify?.routes?.root || '/';
    return fetch(`${rootUrl}search?q=${encodeURIComponent(searchTerm)}&section_id=search-count`, {
      signal: this.abortController.signal,
    })
      .then((res) => (res.ok ? res.text() : null))
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const countEl = doc.querySelector('#search-total-count');
        if (!countEl) return null;
        const totalCount = parseInt(countEl.dataset.count, 10);
        if (isNaN(totalCount) || totalCount <= 0) return null;
        return totalCount;
      })
      .then((count) => {
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedCounts[queryKey] = count;
        });
        return count;
      })
      .catch((error) => {
        if (error?.code === 20) throw error;
        return null;
      });
  }

  applyCountWhenReady(searchTerm, queryKey, countPromise) {
    const promise = countPromise || this.fetchCountForQuery(searchTerm, queryKey);
    promise
      .then((totalCount) => {
        if (this.searchTerm !== searchTerm) return;
        const viewAllItem = this.predictiveSearchResults?.querySelector(
          '.predictive-search__list-item--view-all'
        );
        if (!viewAllItem) return;

        const viewAllLink = viewAllItem.querySelector('a');
        if (viewAllLink && totalCount != null) {
          viewAllLink.textContent = viewAllLink.textContent.replace(/\d+/, String(totalCount));
        }
        viewAllItem.style.visibility = 'visible';
      })
      .catch(() => {});
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegionEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionEl) this.setLiveRegionText(liveRegionEl.textContent);
  }

  getResultsMaxHeight() {
    const headerEl = document.querySelector('.section-header') || document.querySelector('.header');
    this.resultsMaxHeight = window.innerHeight - (headerEl ? headerEl.getBoundingClientRect().bottom : 0);
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    document.body.classList.add('predictive-search-open');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
  
      // Clear predictive results markup
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }
  
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }
  
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
  
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
  
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }
    
    if (typeof this.toggleResetButton === 'function') {
      this.toggleResetButton();
    }

    document.body.classList.remove('predictive-search-open');
  }
}

customElements.define('predictive-search', PredictiveSearch);